'use client';
import React, { useState } from 'react';
import styles from '../styles/EnrollmentModals.module.css';

export default function PaymentModal({
  courseImage,
  courseName,
  dateRange,
  onClose,
  onGoBack,
  sectionId,
  selectedLearner,
  coursePrice,
  weeklyPrice,
  paymentType, // 'ONE_TIME' or 'SUBSCRIPTION'
  couponCode,
  couponValidation,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determine display price based on payment type
  const displayPrice = paymentType === 'ONE_TIME' ? coursePrice : weeklyPrice;
  const paymentTypeLabel = paymentType === 'ONE_TIME' ? 'One-Time Payment' : 'Weekly Subscription';

  // Recalculate effective discount for CURRENT payment type (70% cap rule)
  // This ensures correct values when user switches payment type after applying coupon
  const currentPrice = paymentType === 'ONE_TIME' ? coursePrice : weeklyPrice;
  const rawDiscount = couponValidation?.rawDiscount || 0;
  const maxDiscount = currentPrice * 0.70;
  const effectiveDiscount = Math.min(rawDiscount, maxDiscount);
  const cappedAt70 = rawDiscount > maxDiscount;
  const finalPrice = Math.max(0, currentPrice - effectiveDiscount);

  const handlePayWithStripe = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sectionId,
          learnerId: selectedLearner.id,
          paymentType,
          // Include coupon data if validated - use RECALCULATED values
          ...(couponValidation?.valid && {
            couponCode: couponCode,
            validatedDiscount: {
              effectiveDiscount: effectiveDiscount,  // Recalculated for current payment type
              cappedAt70: cappedAt70,                // Recalculated for current payment type
              promoCodeId: couponValidation.promoCodeId,
              couponId: couponValidation.couponId,
            }
          }),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalDialog}>
        <button className={styles.modalClose} onClick={onClose}>&times;</button>
        <div className={styles.modalHeader}>
          <img src={courseImage} alt="Course" className={styles.courseThumbnail} />
          <div className={styles.courseInfo}>
            <h3>{courseName}</h3>
            <p>{dateRange}</p>
          </div>
        </div>
        <div className={styles.modalContent}>
          <section className={styles.paymentSummary}>
            <h4>Payment Summary</h4>
            <div className={styles.summaryRow}>
              <span>Learner:</span>
              <strong>{selectedLearner?.name || 'Not selected'}</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>Payment Type:</span>
              <strong>{paymentTypeLabel}</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>Amount:</span>
              <strong>
                ${displayPrice?.toFixed(2) || '0.00'}
                {paymentType === 'SUBSCRIPTION' && <span className={styles.perWeek}>/week</span>}
              </strong>
            </div>
          </section>

          {/* Price Summary - show when coupon applied, using RECALCULATED values */}
          {couponValidation?.valid && (
            <div className={styles.priceSummary}>
              <div className={styles.priceRow}>
                <span>Original Price:</span>
                <span className={styles.originalPrice}>
                  ${currentPrice.toFixed(2)}
                </span>
              </div>
              <div className={styles.priceRow}>
                <span>Discount ({couponCode}):</span>
                <span className={styles.discountAmount}>
                  -${effectiveDiscount.toFixed(2)}
                </span>
              </div>
              {cappedAt70 && (
                <p className={styles.cappedNote}>
                  Maximum discount applied
                </p>
              )}
              <div className={`${styles.priceRow} ${styles.totalRow}`}>
                <span>Total:</span>
                <span className={styles.finalPrice}>
                  ${finalPrice.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          <p className={styles.securePayment}>
            Secure payment powered by Stripe
          </p>
        </div>
        <div className={styles.modalFooter}>
          <button
            className={styles.btnCancel}
            onClick={onGoBack}
            disabled={loading}
          >
            Go Back
          </button>
          <button
            className={styles.btnNext}
            onClick={handlePayWithStripe}
            disabled={loading || !selectedLearner}
          >
            {loading ? 'Redirecting...' : `Pay with Stripe`}
          </button>
        </div>
      </div>
    </div>
  );
}
